import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from '@/app/common/components/Card'
import Label from '@/app/common/components/Label'
import { SubmitButton } from '@/app/common/components/SubmitButton'
import { BUSINESS_CATEGORY_OPTIONS } from '@/pages/providers/business-category.constants'
import { useUpdateProviderInfo } from '@/pages/providers/components/hooks/useUpdateProviderInfo.ts'
import { ProviderDetail } from '@/schemas/provider.schema'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shad/ui/select'
import { selectSelectedProgram } from '@/storage/programs/slice'
import { useState } from 'react'
import { useSelector } from 'react-redux'

type Props = {
  provider: ProviderDetail
  providerId: string
}

const ProviderCategoryConfiguration = ({ provider, providerId }: Props) => {
  const programId = useSelector(selectSelectedProgram).id
  const currentCategoryId =
    provider.global_business_category_id?.toString() ?? ''
  const [selectedCategoryId, setSelectedCategoryId] =
    useState(currentCategoryId)

  const updateCategoryMutation = useUpdateProviderInfo({
    providerId,
    programId,
    successTitle: 'Provider Category Updated',
    successDescription:
      'The provider business category has been updated successfully.',
    errorTitle: 'Provider Category Update Failed',
  })

  const onSubmit = () => {
    if (!selectedCategoryId) {
      return
    }

    updateCategoryMutation.mutate({
      global_business_category_id: Number(selectedCategoryId),
    })
  }

  return (
    <Card className="w-full">
      <CardHeader className="font-semibold text-lg">
        Category Configuration
      </CardHeader>

      <CardContent>
        <div className="space-y-2">
          <Label htmlFor="global_business_category_id">Business Category</Label>
          <Select
            value={selectedCategoryId}
            onValueChange={setSelectedCategoryId}
            disabled={updateCategoryMutation.status === 'pending'}
          >
            <SelectTrigger id="global_business_category_id">
              <SelectValue placeholder="Select a business category" />
            </SelectTrigger>
            <SelectContent>
              {BUSINESS_CATEGORY_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardContent>

      <CardFooter className="flex justify-end py-4 border-t border-gray-200">
        <div>
          <SubmitButton
            type="button"
            buttonTxt="Save changes"
            submittingText="Saving..."
            aria-label="save_provider_category"
            status={updateCategoryMutation.status}
            forceDisabled={
              !selectedCategoryId || selectedCategoryId === currentCategoryId
            }
            onClick={onSubmit}
          />
        </div>
      </CardFooter>
    </Card>
  )
}

export default ProviderCategoryConfiguration
